import { forwardRef, type ComponentProps } from "react"
import {
  HoverCardContent as HoverCardContentBase,
  HoverCardPortal,
} from "@/v2-ui/hoverCard/base/HoverCardBase"
import { useQuerySelectorLazy } from "@/v2-ui/utils/utils.dom"
import cn from "@/v2-ui/utils/utils.cn"

const DEFAULT_PORTAL_CONTAINER = "#mount"

export type HoverCardContentProps = ComponentProps<
  typeof HoverCardContentBase
> & {
  skipPortal?: boolean
  portalContainer?: string
}

function HoverCardContent(
  props: HoverCardContentProps,
  ref: HoverCardContentProps["ref"],
) {
  const {
    skipPortal,
    className,
    portalContainer = DEFAULT_PORTAL_CONTAINER,
    onClick,
    onPointerDown,
    ...hoverCardContentBaseProps
  } = props

  const portalElem = useQuerySelectorLazy(portalContainer)

  const content = (
    <HoverCardContentBase
      className={cn("ui-hoverCard-content", className)}
      ref={ref}
      // The card is portalled in the DOM but stays nested under its trigger
      // in the React tree, so clicks inside it bubble to clickable ancestors
      // of the anchor (e.g. a table row's expand onClick) and activate them.
      // Stop at the card boundary — inner handlers have already run by then,
      // and native behavior (link navigation, new tab) is untouched.
      onClick={(e) => {
        onClick?.(e)
        e.stopPropagation()
      }}
      onPointerDown={(e) => {
        onPointerDown?.(e)
        e.stopPropagation()
      }}
      {...hoverCardContentBaseProps}
    />
  )

  return skipPortal ? (
    content
  ) : (
    <HoverCardPortal container={portalElem}>{content}</HoverCardPortal>
  )
}

export default forwardRef(HoverCardContent)
